import React, { useState, useEffect } from 'react';
import './ScanWizard.css';

const STEPS = [
  'Goal',
  'Target',
  'Scanner',
  'Profile',
  'Schedule'
];

const SCAN_TYPES = [
  { id: 'full', title: 'Full Spectrum', icon: '⚡', description: 'Deep analysis: SAST, SCA, IaC, and Network Traffic.' },
  { id: 'code', title: 'Code Only', icon: '💻', description: 'Focus on SAST/Taint Tracking for logic flaws and secrets.' },
  { id: 'config', title: 'Config & IaC', icon: '🔧', description: 'Audit environment settings and infrastructure scripts.' },
  { id: 'traffic', title: 'Network Traffic', icon: '🌐', description: 'Analyze PCAP files for lateral movement and exfiltration.' }
];

const ScanWizard = ({ isOpen, onClose, onComplete, initialData = {}, isSentinel = false, onBrowse }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    target_path: '',
    scan_type: 'full',
    scan_location: isSentinel ? 'agent' : 'hub',
    scan_source: 'filesystem',
    scan_mode: 'internal',
    frequency: 'once', // 'once' or 'daily', 'weekly', etc.
    email_alerts_enabled: true,
    ...initialData
  });

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Synchronize target path if it's updated via the prop from the parent (picker selection)
  useEffect(() => {
    if (initialData.target_path && initialData.target_path !== formData.target_path) {
      setFormData(prev => ({ ...prev, target_path: initialData.target_path }));
    }
  }, [initialData.target_path, formData.target_path]);

  if (!isOpen) return null;

  const renderStep = () => {
    switch (currentStep) {
      case 0: // Goal
        return (
          <div className="wizard-step-content">
            <h3>Define your objective</h3>
            <p className="step-description">What type of environment are you securing today?</p>
            <div className="wizard-options-grid">
              <div 
                className={`wizard-option-card ${formData.scan_mode === 'internal' ? 'selected' : ''}`}
                onClick={() => setFormData({...formData, scan_mode: 'internal'})}
              >
                <div className="option-icon">🏢</div>
                <h4>Internal Asset</h4>
                <p>Analyze source code, local configurations, or internal network traffic.</p>
              </div>
              <div 
                className={`wizard-option-card ${formData.scan_mode === 'external' ? 'selected' : ''}`}
                onClick={() => setFormData({...formData, scan_mode: 'external'})}
              >
                <div className="option-icon">🌍</div>
                <h4>External Surface</h4>
                <p>Map internet-facing domains and exposed services (Discovery scan).</p>
              </div>
            </div>
          </div>
        );

      case 1: // Target
        return (
          <div className="wizard-step-content">
            <h3>Select the target</h3>
            <p className="step-description">Where is the data or code located?</p>
            
            {formData.scan_mode === 'internal' ? (
              <div className="wizard-options-grid">
                <div 
                  className={`wizard-option-card ${formData.scan_source === 'filesystem' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, scan_source: 'filesystem'})}
                >
                  <div className="option-icon">📂</div>
                  <h4>Local Path</h4>
                  <p>A directory on your machine or the Edge Agent's host.</p>
                </div>
                <div 
                  className={`wizard-option-card ${formData.scan_source === 'repository' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, scan_source: 'repository'})}
                >
                  <div className="option-icon">🐙</div>
                  <h4>Git Repository</h4>
                  <p>Scan a remote repository via HTTPS (Public or Private).</p>
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label>External Domain</label>
                <input 
                  type="text" 
                  placeholder="e.g. passiveguard.io"
                  value={formData.external_domain || ''}
                  onChange={(e) => setFormData({...formData, external_domain: e.target.value})}
                />
              </div>
            )}

            <div className="form-group" style={{ marginTop: '24px' }}>
              <label>Scan Friendly Name</label>
              <input 
                type="text" 
                placeholder="Business-Critical App Scan"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
          </div>
        );

      case 2: // Scanner Location
        return (
          <div className="wizard-step-content">
            <h3>Choose Scanner Location</h3>
            <p className="step-description">Where should the heavy analysis take place?</p>
            <div className="wizard-options-grid">
              <div 
                className={`wizard-option-card ${formData.scan_location === 'agent' ? 'selected' : ''}`}
                onClick={() => setFormData({...formData, scan_location: 'agent'})}
              >
                <div className="option-icon">🏠</div>
                <h4>Edge Agent (Recommended)</h4>
                <p>Analysis stays in your network. Source code never leaves your infrastructure.</p>
                <span className="badge-info" style={{ marginTop: '10px' }}>PRO-PRIVACY</span>
              </div>
              {!isSentinel && (
                <div 
                  className={`wizard-option-card ${formData.scan_location === 'hub' ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, scan_location: 'hub'})}
                >
                  <div className="option-icon">☁️</div>
                  <h4>Cloud Hub</h4>
                  <p>PassiveGuard infrastructure performs the scan. Best for lightweight public asset checks.</p>
                </div>
              )}
            </div>

            {formData.scan_source === 'filesystem' && (
              <div className="form-group" style={{ marginTop: '24px' }}>
                <label>Target Path</label>
                <div className="input-with-button">
                  <input 
                    type="text" 
                    placeholder="/path/to/project"
                    value={formData.target_path}
                    readOnly
                  />
                  <button className="browse-btn" onClick={() => onBrowse(formData.scan_location)}>BROWSE</button>
                </div>
              </div>
            )}
          </div>
        );

      case 3: // Scan Profile
        return (
          <div className="wizard-step-content">
            <h3>Select Scan Profile</h3>
            <p className="step-description">Choose the depth of the analysis.</p>
            <div className="wizard-options-grid">
              {SCAN_TYPES.map(type => (
                <div 
                  key={type.id}
                  className={`wizard-option-card ${formData.scan_type === type.id ? 'selected' : ''}`}
                  onClick={() => setFormData({...formData, scan_type: type.id})}
                >
                  <div className="option-icon">{type.icon}</div>
                  <h4>{type.title}</h4>
                  <p>{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 4: // Schedule & Summary
        return (
          <div className="wizard-step-content">
            <h3>Execution & Schedule</h3>
            <p className="step-description">When should this scan run?</p>
            
            <div className="form-group">
              <label>Frequency</label>
              <select 
                value={formData.frequency}
                onChange={(e) => setFormData({...formData, frequency: e.target.value})}
              >
                <option value="once">Run Once (Manual)</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>

            <div className="summary-card">
              <h4>Scan Summary</h4>
              <div className="summary-item">
                <span className="summary-label">Name</span>
                <span className="summary-value">{formData.name || 'Untitled Scan'}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Target</span>
                <span className="summary-value">{formData.scan_mode === 'internal' ? formData.target_path || 'Repository' : formData.external_domain}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Engine</span>
                <span className="summary-value">{formData.scan_location === 'agent' ? 'Edge Agent' : 'Cloud Hub'}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Profile</span>
                <span className="summary-value">{SCAN_TYPES.find(t => t.id === formData.scan_type)?.title}</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="scan-wizard-overlay">
      <div className="scan-wizard-container">
        <div className="wizard-header">
          <h2>
            <span style={{ color: 'var(--accent-blue)' }}>🛡️</span>
            {formData.frequency === 'once' ? 'New Vulnerability Scan' : 'Schedule Recurring Scan'}
          </h2>
          <button className="wizard-close" onClick={onClose}>&times;</button>
        </div>

        <div className="wizard-progress">
          {STEPS.map((step, idx) => (
            <div 
              key={step} 
              className={`progress-step ${idx === currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`}
            >
              {step}
            </div>
          ))}
        </div>

        <div className="wizard-body">
          {renderStep()}
        </div>

        <div className="wizard-footer">
          <button 
            className="wizard-btn-back" 
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            BACK
          </button>
          
          {currentStep === STEPS.length - 1 ? (
            <button 
              className="wizard-btn-next" 
              onClick={() => onComplete(formData)}
              disabled={!formData.name}
            >
              LAUNCH SCAN
            </button>
          ) : (
            <button 
              className="wizard-btn-next" 
              onClick={nextStep}
              disabled={
                (currentStep === 1 && formData.scan_mode === 'internal' && formData.scan_source === 'filesystem' && !formData.name) ||
                (currentStep === 1 && formData.scan_mode === 'external' && !formData.external_domain)
              }
            >
              CONTINUE
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScanWizard;
